import { useMemo } from 'react';

import { useAppSelector } from 'flow/hooks/useRedux';

import { getExtensions } from 'flow/selectors/ExtensionSelectors';
import { extractIndex } from 'flow/utils/extension-utils';
import { hasVideoCapability } from 'flow/utils/extension-utils-TW';
import { sortByName } from 'flow/utils';

import { ExtensionKey } from 'flow/types/EntityKeys';
import { Extension } from 'flow/types/Extension';
import { TypedMap } from 'flow/types/TypedMap';

const useParticipantCandidates = (
    ownKey: ExtensionKey | undefined,
    selectedKeys: ExtensionKey[],
    query: string,
): ExtensionKey[] => {
    const extensions = useAppSelector(getExtensions);

    const candidateExtensions = useMemo(() => {
        const sorted = extensions
            .filter(
                (e: TypedMap<Extension>) =>
                    e.get('key') !== ownKey && hasVideoCapability(e),
            )
            .sort(sortByName)
            .valueSeq()
            .toArray();

        return sorted.map((ext) => ({
            ext,
            searchIndex: extractIndex(ext, true, false),
        }));
    }, [extensions, ownKey]);

    return useMemo<ExtensionKey[]>(() => {
        const selectedSet = new Set(selectedKeys);
        const needle = query.trim().toLowerCase();
        return candidateExtensions
            .filter(
                ({ ext, searchIndex }) =>
                    !selectedSet.has(ext.get('key')) &&
                    (!needle || searchIndex.indexOf(needle) !== -1),
            )
            .map(({ ext }) => ext.get('key'));
    }, [candidateExtensions, selectedKeys, query]);
};

export default useParticipantCandidates;
